import {
	type CaptureFileJSONObject,
	type CollectionFieldAnchorManualFieldValue,
	type CollectionFieldAnchorP2PFieldValue,
	type CollectionModelFieldAnchorValue,
	type DistanceBearingPitch,
	IkeFieldType,
	type LocationAltitude,
	type LocationData,
	type LocationDataRaw,
} from '@ikegps/core-api';
import type { AnchorLocationData } from '@ikegps/core-api/src/common/anchor';
import type { AnchorPlacement } from '@ikegps/core-api/src/common/orientation';
import type { CollectionFieldAnchorLocationValue } from '@ikegps/core-api/src/public/collection.field';
import { IkeGeodesic } from '../../../geo/geodesic';
import { MultiPoint } from '../../../math';
import { IkeAngle } from '../../../unit';
import type { FormModelField } from '../../form/form.field';
import { CollectionModelField, ModelMutator } from '../collection.field';
import { registerFieldType } from './field.registry';
import { CollectionModelFieldLocation, extractLocationRaw } from './location.field';
import type { CaptureProvider } from './provider/capture.provider';

export type ModelFieldStateAnchor =
	| ModelFieldStateAnchorManual
	| ModelFieldStateAnchorP2P
	| ModelFieldStateAnchorLocation;

export interface ModelFieldStateAnchorManual {
	provider: 'manual';
	value: CollectionFieldAnchorManualFieldValue;
}

export interface ModelFieldStateAnchorLocation extends Partial<LocationDataRaw> {
	provider: 'manual' | 'gps' | 'edited';
	value: CollectionFieldAnchorLocationValue;
}

export interface ModelFieldStateAnchorP2P {
	provider: 'pointtopoint';
	/** List of captures used for P2P (Generally two) */
	captures: string[];
	value: {
		collectionTool: 'VECTOR';
		placement: AnchorPlacement;
		relatedSpanIds?: string[];
	};
}

export class ModelMutatorAnchor extends ModelMutator<FormModelField, CollectionModelFieldAnchor> {
	// Mutator to edit a vector anchor manually
	manualAnchor(newValue: CollectionFieldAnchorManualFieldValue | null) {
		if (newValue == null || newValue.distance == null || newValue.bearing == null) {
			this.state.setFieldValue<ModelFieldStateAnchorManual>(this.field.id, null);
			return;
		}

		// Force the anchor bearing to be between 0 and two Pi
		newValue.bearing = IkeAngle.normalize(newValue.bearing);

		// Sometimes pitch is not set, It is a required field
		if (newValue.pitch == null || isNaN(newValue.pitch)) {
			newValue.pitch = 0;
		}

		newValue.collectionTool = CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR;
		this.state.setFieldValue<ModelFieldStateAnchorManual>(this.field.id, {
			provider: CollectionModelFieldAnchor.PROVIDER_MANUAL,
			value: newValue,
		});
	}

	// Mutator when adding a new p2p vector
	p2p(state: string[], placement: AnchorPlacement) {
		if (!Array.isArray(state)) {
			throw new Error(`Field ${this.field.id}: Invalid capture state.`);
		}
		if (state.length < 2) {
			throw new Error(`Field ${this.field.id}: Invalid capture count "${state.length}".`);
		}

		this.state.setFieldValue<ModelFieldStateAnchorP2P>(this.field.id, {
			provider: CollectionModelFieldAnchor.PROVIDER_P2P,
			captures: state.slice(),
			value: {
				collectionTool: CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR,
				placement,
			},
		});
	}

	// Method used by viewVectorManualEdit
	manual(newValue: CollectionFieldAnchorManualFieldValue) {
		if (newValue == null || newValue.distance == null || newValue.bearing == null) {
			this.state.setFieldValue<ModelFieldStateAnchorManual>(this.field.id, null);
			return;
		}

		const anchorManualData: CollectionFieldAnchorManualFieldValue = {
			collectionTool: CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR,
			placement: newValue.placement,
			distance: newValue.distance,
			bearing: newValue.bearing,
			pitch: newValue.pitch,
			relatedSpanIds: newValue.relatedSpanIds,
		};
		this.manualAnchor(anchorManualData);
	}

	// Mutator when adding anchor with gps position
	gps(newValue: AnchorLocationData) {
		// Validate GPS coordinates
		if (!CollectionModelFieldLocation.isValidLocation(newValue)) {
			this.clear();
			return;
		}

		const value: AnchorLocationData = {
			latitude: newValue.latitude,
			longitude: newValue.longitude,
			altitude: newValue.altitude,
			collectionTool: newValue.collectionTool,
			placement: newValue.placement,
			relatedSpanIds: newValue.relatedSpanIds,
		};

		if (newValue.accuracy != null) {
			value.accuracy = newValue.accuracy;
		}

		const stateValue: ModelFieldStateAnchorLocation = {
			provider: 'gps',
			value,
		};

		this.state.setFieldValue(this.field.id, stateValue);
	}

	// Set anntenaHeight value
	setAntennaHeight(antennaHeight) {
		var stateValue = this.state.getFieldValue(this.field.id) as ModelFieldStateAnchorLocation;
		stateValue.antennaHeight = antennaHeight;
	}

	override copy(from: CollectionModelFieldAnchor): boolean {
		if (from.isPointToPoint()) {
			this.p2p(from.stateValue.captures, from.value.placement);
			return true;
		}

		if (from.isManual()) {
			this.manualAnchor(from.value);
			return true;
		}

		return false;
	}
}

export class CollectionModelFieldAnchor
	extends CollectionModelField<FormModelField, ModelFieldStateAnchor, CollectionModelFieldAnchorValue>
	implements CaptureProvider
{
	static readonly PROVIDER_MANUAL = 'manual' as const;
	static readonly PROVIDER_P2P = 'pointtopoint' as const;
	static readonly PROVIDER_GPS = 'gps';
	static readonly PROVIDER_EDITED = 'edited';

	static readonly COLLLECTION_TOOL_VECTOR = 'VECTOR';
	static readonly COLLLECTION_TOOL_LOCATION = 'LOCATION';

	static readonly DEFAULT_PLACEMENT: AnchorPlacement = 'Other';

	static TYPE: IkeFieldType = IkeFieldType.ANCHOR;

	static isAnchorField(f: CollectionModelField): f is CollectionModelFieldAnchor {
		return f.schema.type === CollectionModelFieldAnchor.TYPE;
	}

	static isCollectionWithVectorTool(
		value: CollectionModelFieldAnchorValue
	): value is CollectionFieldAnchorManualFieldValue {
		return value.collectionTool === CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR;
	}

	static isCollectionWithLocationTool(value: CollectionModelFieldAnchorValue): value is AnchorLocationData {
		return value.collectionTool === CollectionModelFieldAnchor.COLLLECTION_TOOL_LOCATION;
	}

	private _Mutator: ModelMutatorAnchor;
	get Mutator() {
		if (this._Mutator == null) {
			this._Mutator = new ModelMutatorAnchor(this);
		}
		return this._Mutator;
	}

	override get value(): CollectionModelFieldAnchorValue {
		const stateValue = this.stateValue;
		if (stateValue == null) {
			return null;
		}

		if (stateValue.value.collectionTool === CollectionModelFieldAnchor.COLLLECTION_TOOL_LOCATION) {
			// If a placement is not provided, then we want to default to Other
			const locationState = stateValue.value;
			locationState.placement = locationState.placement || CollectionModelFieldAnchor.DEFAULT_PLACEMENT;
			return locationState;
		}

		if (stateValue.value.collectionTool === CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR) {
			if (this.isManual()) {
				return <CollectionFieldAnchorManualFieldValue>stateValue.value;
			}

			if (this.isPointToPoint()) {
				const vectorValue = MultiPoint.Points(this.getCaptures()) as CollectionFieldAnchorP2PFieldValue;
				// To be backwards compatible, vertical values should not have a sign on them
				vectorValue.vertical = Math.abs(vectorValue.vertical);
				vectorValue.collectionTool = CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR;
				vectorValue.placement = stateValue.value.placement;
				return vectorValue;
			}
		}

		throw new Error(`Field ${this.id}: Invalid Provider type "${stateValue['provider']}"`);
	}

	/**
	 * Get the raw location data calculating alternative altitudes
	 *
	 * @remarks This cannot be null
	 */
	get raw(): Partial<LocationDataRaw> & Partial<LocationAltitude> {
		if (!this.isGPS()) {
			return {};
		}

		const stateValue = this.stateValue;
		const isAntennaHeightMissing = stateValue.antennaHeight == null || stateValue.antennaHeight === 0;
		if (isAntennaHeightMissing) {
			return {
				...extractLocationRaw(stateValue),
				deviceAltitude: stateValue.value.altitude,
				antennaHeight: undefined, // Destroy antennaHeight if it is 0
			};
		}

		return {
			...extractLocationRaw(stateValue),
			deviceAltitude: stateValue.antennaHeight + stateValue.value.altitude,
			groundAltitude: stateValue.value.altitude,
			antennaHeight: stateValue.antennaHeight,
		};
	}

	get provider(): 'manual' | 'pointtopoint' | 'gps' | 'edited' {
		const stateValue = this.stateValue;
		if (stateValue == null || stateValue.provider == null) {
			return CollectionModelFieldAnchor.PROVIDER_MANUAL;
		}
		return stateValue.provider;
	}

	get antennaHeight(): number {
		const stateValue = this.stateValue as ModelFieldStateAnchorLocation;
		if (stateValue == null || stateValue.antennaHeight == null) {
			return 0;
		}
		return stateValue.antennaHeight;
	}

	target(source: LocationData): LocationData {
		if (CollectionModelFieldAnchor.isCollectionWithVectorTool(this.value)) {
			const bearing = this.value.bearing;
			const distance = this.value.distance;
			return IkeGeodesic.projectLocation(source, bearing, distance);
		}

		if (CollectionModelFieldAnchor.isCollectionWithLocationTool(this.value)) {
			return {
				altitude: this.value.altitude,
				longitude: this.value.longitude,
				latitude: this.value.latitude,
			};
		}
		return null;
	}

	distanceBearingPitch(source: LocationData): DistanceBearingPitch | null {
		if (!this.value) {
			return {
				distance: 0,
				bearing: 0,
				pitch: 0,
			};
		}

		if (CollectionModelFieldAnchor.isCollectionWithVectorTool(this.value)) {
			return {
				distance: this.value.distance,
				bearing: this.value.bearing,
				pitch: this.value.pitch,
			};
		}
		const stateValue = this.stateValue;
		if (!stateValue) {
			return null;
		}
		if (source == null) {
			return null;
		}

		if (CollectionModelFieldAnchor.isCollectionWithLocationTool(this.value)) {
			return IkeGeodesic.getVector(source, this.stateValue.value as LocationData);
		}
		return null;
	}

	isManual(): this is CollectionModelField<
		FormModelField,
		ModelFieldStateAnchorManual,
		CollectionFieldAnchorManualFieldValue
	> {
		return this.provider === CollectionModelFieldAnchor.PROVIDER_MANUAL;
	}

	isPointToPoint(): this is CollectionModelField<
		FormModelField,
		ModelFieldStateAnchorP2P,
		CollectionFieldAnchorP2PFieldValue
	> {
		return this.provider === CollectionModelFieldAnchor.PROVIDER_P2P;
	}

	isGPS(): this is CollectionModelField<FormModelField, ModelFieldStateAnchorLocation, AnchorLocationData> {
		return this.provider === CollectionModelFieldAnchor.PROVIDER_GPS;
	}

	isTrueSize(): this is CollectionModelField<FormModelField, ModelFieldStateAnchorLocation> {
		return false;
	}

	// Get tool used for collection, by default we show a Vector tool
	get tool(): 'LOCATION' | 'VECTOR' {
		const stateValue = this.stateValue;
		if (stateValue == null || stateValue.value.collectionTool == null) {
			return CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR;
		}

		return stateValue.value.collectionTool;
	}

	getOwnedCaptures(): CaptureFileJSONObject[] {
		return this.getCaptures();
	}

	getCaptures(): CaptureFileJSONObject[] {
		const captures: CaptureFileJSONObject[] = [];
		const captureIds = this.getCaptureIds();
		for (const captureId of captureIds) {
			const capture = this.collection.state.getCapture(captureId);
			if (capture == null) {
				return [];
			}

			if (capture.orientation == null) {
				throw new Error(`Invalid truesize capture '${capture.objectId}' for field '${this.id}' (missing orientation)`);
			}

			if (capture.calibrationid == null) {
				throw new Error(
					`Invalid truesize capture '${capture.objectId}' for field '${this.id}' (missing calibrationId)`
				);
			}

			captures.push(capture);
		}
		return captures;
	}

	isCollectionLink(): boolean {
		return false;
	}

	getOwnedCaptureIds(): string[] {
		return this.getCaptureIds();
	}
	getCaptureIds(): string[] {
		if (!this.isPointToPoint()) {
			return [];
		}
		return this.stateValue.captures;
	}

	hasValue(): boolean {
		if (this.value != null) {
			if (this.tool === CollectionModelFieldAnchor.COLLLECTION_TOOL_LOCATION) {
				return CollectionModelFieldLocation.isValidLocation(<AnchorLocationData>this.value);
			}
			if (
				(<CollectionFieldAnchorManualFieldValue>this.value).distance === 0 &&
				(<CollectionFieldAnchorManualFieldValue>this.value).bearing === 0 &&
				(<CollectionFieldAnchorManualFieldValue>this.value).placement === null
			) {
				return false;
			}
			return true;
		}
		return false;
	}
}

registerFieldType(IkeFieldType.ANCHOR, (parent, schema) => new CollectionModelFieldAnchor(parent, schema));
